import * as C from "../constants/serviceConstants";
import api from "../utils/axiosInstance";
import { getApiErrorMessage } from "../utils/errorMessages";

const getErrorMessage = (error, fallback) =>
  getApiErrorMessage(error, { fallback });

const normalizeString = (value = "") => String(value || "").trim();

const encodePath = (value = "") => encodeURIComponent(normalizeString(value));

const buildIdempotencyKey = (prefix = "service") =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const getCheckoutUrl = (payload = {}) =>
  normalizeString(
    payload.checkoutUrl ||
      payload.hostedCheckoutUrl ||
      payload.approvalUrl ||
      payload.approveUrl ||
      payload.payerActionUrl ||
      payload.nextAction?.url,
  );

function buildClientError(message, code = "BAD_REQUEST", status = 400) {
  const error = new Error(message);
  error.code = code;
  error.status = status;
  return error;
}

export const listServices = () => async (dispatch) => {
  try {
    dispatch({ type: C.SERVICE_LIST_REQUEST });

    const { data } = await api.get("/services");

    dispatch({
      type: C.SERVICE_LIST_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not load services right now. Please try again later.",
    );

    dispatch({
      type: C.SERVICE_LIST_FAIL,
      payload,
    });

    throw error;
  }
};

export const createServiceBooking = (booking) => async (dispatch) => {
  try {
    dispatch({ type: C.SERVICE_BOOKING_CREATE_REQUEST });

    const { data } = await api.post("/services/bookings", booking);

    dispatch({
      type: C.SERVICE_BOOKING_CREATE_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not reserve that appointment right now. Please try again later.",
    );

    dispatch({
      type: C.SERVICE_BOOKING_CREATE_FAIL,
      payload,
    });

    throw error;
  }
};

export const quoteServiceOrder = (payload) => async (dispatch) => {
  try {
    dispatch({ type: C.SERVICE_ORDER_QUOTE_REQUEST });

    const { data } = await api.post("/services/orders/quote", payload);

    dispatch({
      type: C.SERVICE_ORDER_QUOTE_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const errorPayload = getErrorMessage(
      error,
      "We could not calculate that service quote right now. Please try again later.",
    );

    dispatch({
      type: C.SERVICE_ORDER_QUOTE_FAIL,
      payload: errorPayload,
    });

    throw error;
  }
};

export const createServiceOrder = (payload) => async (dispatch) => {
  const idempotencyKey =
    normalizeString(payload?.idempotencyKey) ||
    buildIdempotencyKey("service-order");

  try {
    dispatch({ type: C.SERVICE_ORDER_CREATE_REQUEST });

    const { data } = await api.post(
      "/services/orders",
      {
        ...payload,
        idempotencyKey,
      },
      {
        headers: {
          "Idempotency-Key": idempotencyKey,
        },
      },
    );

    const normalized = {
      ...data,
      checkoutUrl: getCheckoutUrl(data),
      idempotencyKey,
    };

    dispatch({
      type: C.SERVICE_ORDER_CREATE_SUCCESS,
      payload: normalized,
    });

    return normalized;
  } catch (error) {
    const errorPayload = getErrorMessage(
      error,
      "We could not start this service order right now. Please try again later.",
    );

    dispatch({
      type: C.SERVICE_ORDER_CREATE_FAIL,
      payload: errorPayload,
    });

    throw error;
  }
};

export const getServiceOrder = (reference) => async (dispatch) => {
  const safeReference = normalizeString(reference);

  try {
    dispatch({
      type: C.SERVICE_ORDER_DETAILS_REQUEST,
      meta: { reference: safeReference },
    });

    if (!safeReference) {
      throw buildClientError(
        "Service order reference is required.",
        "SERVICE_ORDER_REFERENCE_REQUIRED",
      );
    }

    const { data } = await api.get(
      `/services/orders/${encodePath(safeReference)}`,
    );

    dispatch({
      type: C.SERVICE_ORDER_DETAILS_SUCCESS,
      payload: data,
      meta: { reference: safeReference },
    });

    return data;
  } catch (error) {
    const errorPayload = getErrorMessage(
      error,
      "We could not load this service order right now. Please try again later.",
    );

    dispatch({
      type: C.SERVICE_ORDER_DETAILS_FAIL,
      payload: errorPayload,
      meta: { reference: safeReference },
    });

    throw error;
  }
};

export const createServiceOrderPayment =
  (reference, payload = {}) =>
  async (dispatch) => {
    const safeReference = normalizeString(reference);
    const idempotencyKey =
      normalizeString(payload?.idempotencyKey) ||
      buildIdempotencyKey("service-payment");

    try {
      dispatch({
        type: C.SERVICE_ORDER_PAYMENT_REQUEST,
        meta: { reference: safeReference },
      });

      if (!safeReference) {
        throw buildClientError(
          "Service order reference is required.",
          "SERVICE_ORDER_REFERENCE_REQUIRED",
        );
      }

      const { data } = await api.post(
        `/services/orders/${encodePath(safeReference)}/payment`,
        {
          ...payload,
          idempotencyKey,
        },
        {
          headers: {
            "Idempotency-Key": idempotencyKey,
          },
        },
      );

      const normalized = {
        ...data,
        checkoutUrl: getCheckoutUrl(data),
        idempotencyKey,
      };

      dispatch({
        type: C.SERVICE_ORDER_PAYMENT_SUCCESS,
        payload: normalized,
        meta: { reference: safeReference },
      });

      return normalized;
    } catch (error) {
      const errorPayload = getErrorMessage(
        error,
        "We could not start secure payment right now. Please try again later.",
      );

      dispatch({
        type: C.SERVICE_ORDER_PAYMENT_FAIL,
        payload: errorPayload,
        meta: { reference: safeReference },
      });

      throw error;
    }
  };

export const checkServiceOrderPaymentStatus =
  (reference, { provider = "" } = {}) =>
  async (dispatch) => {
    const safeReference = normalizeString(reference);

    try {
      dispatch({
        type: C.SERVICE_ORDER_PAYMENT_REQUEST,
        meta: { reference: safeReference },
      });

      if (!safeReference) {
        throw buildClientError(
          "Service order reference is required.",
          "SERVICE_ORDER_REFERENCE_REQUIRED",
        );
      }

      const { data } = await api.get(
        `/services/orders/${encodePath(safeReference)}/payment-status`,
        {
          params: normalizeString(provider) ? { provider } : {},
        },
      );

      dispatch({
        type: C.SERVICE_ORDER_PAYMENT_SUCCESS,
        payload: data,
        meta: { reference: safeReference },
      });

      dispatch({
        type: C.SERVICE_ORDER_DETAILS_SUCCESS,
        payload: data,
        meta: { reference: safeReference },
      });

      return data;
    } catch (error) {
      const errorPayload = getErrorMessage(
        error,
        "We could not refresh payment status right now. Please try again later.",
      );

      dispatch({
        type: C.SERVICE_ORDER_PAYMENT_FAIL,
        payload: errorPayload,
        meta: { reference: safeReference },
      });

      throw error;
    }
  };

export const addServiceOrderMessage =
  (reference, message) => async (dispatch) => {
    const safeReference = normalizeString(reference);

    try {
      dispatch({
        type: C.SERVICE_ORDER_MESSAGE_REQUEST,
        meta: { reference: safeReference },
      });

      if (!safeReference) {
        throw buildClientError(
          "Service order reference is required.",
          "SERVICE_ORDER_REFERENCE_REQUIRED",
        );
      }

      const { data } = await api.post(
        `/services/orders/${encodePath(safeReference)}/messages`,
        { message: normalizeString(message) },
      );

      dispatch({
        type: C.SERVICE_ORDER_MESSAGE_SUCCESS,
        payload: data,
        meta: { reference: safeReference },
      });

      dispatch({
        type: C.SERVICE_ORDER_DETAILS_SUCCESS,
        payload: data,
        meta: { reference: safeReference },
      });

      return data;
    } catch (error) {
      const errorPayload = getErrorMessage(
        error,
        "We could not send your message right now. Please try again later.",
      );

      dispatch({
        type: C.SERVICE_ORDER_MESSAGE_FAIL,
        payload: errorPayload,
        meta: { reference: safeReference },
      });

      throw error;
    }
  };

export const respondToServiceOrderAdjustment =
  (reference, adjustmentId, decision) => async (dispatch) => {
    const safeReference = normalizeString(reference);
    const safeAdjustmentId = normalizeString(adjustmentId);

    try {
      dispatch({
        type: C.SERVICE_ORDER_ADJUSTMENT_RESPONSE_REQUEST,
        meta: { reference: safeReference, adjustmentId: safeAdjustmentId },
      });

      if (!safeReference || !safeAdjustmentId) {
        throw buildClientError(
          "Service order adjustment is required.",
          "SERVICE_ORDER_ADJUSTMENT_REQUIRED",
        );
      }

      const { data } = await api.post(
        `/services/orders/${encodePath(safeReference)}/adjustments/${encodePath(
          safeAdjustmentId,
        )}/respond`,
        { decision },
      );

      dispatch({
        type: C.SERVICE_ORDER_ADJUSTMENT_RESPONSE_SUCCESS,
        payload: data,
        meta: { reference: safeReference, adjustmentId: safeAdjustmentId },
      });

      dispatch({
        type: C.SERVICE_ORDER_DETAILS_SUCCESS,
        payload: data,
        meta: { reference: safeReference },
      });

      return data;
    } catch (error) {
      const errorPayload = getErrorMessage(
        error,
        "We could not update that adjustment right now. Please try again later.",
      );

      dispatch({
        type: C.SERVICE_ORDER_ADJUSTMENT_RESPONSE_FAIL,
        payload: errorPayload,
        meta: { reference: safeReference, adjustmentId: safeAdjustmentId },
      });

      throw error;
    }
  };

export const listServicesAdmin = () => async (dispatch) => {
  try {
    dispatch({ type: C.ADMIN_SERVICE_LIST_REQUEST });

    const { data } = await api.get("/services/admin");

    dispatch({
      type: C.ADMIN_SERVICE_LIST_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not load admin services right now. Please try again later.",
    );

    dispatch({
      type: C.ADMIN_SERVICE_LIST_FAIL,
      payload,
    });

    throw error;
  }
};

export const getServiceDetailsAdmin = (id) => async (dispatch) => {
  const safeId = normalizeString(id);

  try {
    dispatch({
      type: C.ADMIN_SERVICE_DETAILS_REQUEST,
      meta: { id: safeId },
    });

    if (!safeId) {
      throw buildClientError("Service id is required.", "SERVICE_ID_REQUIRED");
    }

    const { data } = await api.get(`/services/admin/${encodePath(safeId)}`);

    dispatch({
      type: C.ADMIN_SERVICE_DETAILS_SUCCESS,
      payload: data,
      meta: { id: safeId },
    });

    return data;
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not load this service right now. Please try again later.",
    );

    dispatch({
      type: C.ADMIN_SERVICE_DETAILS_FAIL,
      payload,
      meta: { id: safeId },
    });

    throw error;
  }
};

export const createServiceAdmin = (service) => async (dispatch) => {
  try {
    dispatch({ type: C.ADMIN_SERVICE_CREATE_REQUEST });

    const { data } = await api.post("/services/admin", service);

    dispatch({
      type: C.ADMIN_SERVICE_CREATE_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not create this service right now. Please try again later.",
    );

    dispatch({
      type: C.ADMIN_SERVICE_CREATE_FAIL,
      payload,
    });

    throw error;
  }
};

export const updateServiceAdmin = (id, service) => async (dispatch) => {
  const safeId = normalizeString(id);

  try {
    dispatch({
      type: C.ADMIN_SERVICE_UPDATE_REQUEST,
      meta: { id: safeId },
    });

    if (!safeId) {
      throw buildClientError("Service id is required.", "SERVICE_ID_REQUIRED");
    }

    const { data } = await api.patch(
      `/services/admin/${encodePath(safeId)}`,
      service,
    );

    dispatch({
      type: C.ADMIN_SERVICE_UPDATE_SUCCESS,
      payload: data,
      meta: { id: safeId },
    });

    return data;
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not update this service right now. Please try again later.",
    );

    dispatch({
      type: C.ADMIN_SERVICE_UPDATE_FAIL,
      payload,
      meta: { id: safeId },
    });

    throw error;
  }
};

export const deleteServiceAdmin = (id) => async (dispatch) => {
  const safeId = normalizeString(id);

  try {
    dispatch({
      type: C.ADMIN_SERVICE_DELETE_REQUEST,
      meta: { id: safeId },
    });

    if (!safeId) {
      throw buildClientError("Service id is required.", "SERVICE_ID_REQUIRED");
    }

    await api.delete(`/services/admin/${encodePath(safeId)}`);

    dispatch({
      type: C.ADMIN_SERVICE_DELETE_SUCCESS,
      payload: { id: safeId },
      meta: { id: safeId },
    });

    return { id: safeId };
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not delete this service right now. Please try again later.",
    );

    dispatch({
      type: C.ADMIN_SERVICE_DELETE_FAIL,
      payload,
      meta: { id: safeId },
    });

    throw error;
  }
};

export const listServiceBookingsAdmin =
  (status = "all") =>
  async (dispatch) => {
    try {
      dispatch({ type: C.ADMIN_SERVICE_BOOKING_LIST_REQUEST });

      const safeStatus = normalizeString(status).toLowerCase() || "all";
      const { data } = await api.get("/services/admin/bookings", {
        params: { status: safeStatus },
      });

      dispatch({
        type: C.ADMIN_SERVICE_BOOKING_LIST_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payload = getErrorMessage(
        error,
        "We could not load service bookings right now. Please try again later.",
      );

      dispatch({
        type: C.ADMIN_SERVICE_BOOKING_LIST_FAIL,
        payload,
      });

      throw error;
    }
  };

export const updateServiceBookingAdmin = (id, patch) => async (dispatch) => {
  const safeId = normalizeString(id);

  try {
    dispatch({
      type: C.ADMIN_SERVICE_BOOKING_UPDATE_REQUEST,
      meta: { id: safeId },
    });

    if (!safeId) {
      throw buildClientError(
        "Booking id is required.",
        "SERVICE_BOOKING_ID_REQUIRED",
      );
    }

    const { data } = await api.patch(
      `/services/admin/bookings/${encodePath(safeId)}`,
      patch,
    );

    dispatch({
      type: C.ADMIN_SERVICE_BOOKING_UPDATE_SUCCESS,
      payload: data,
      meta: { id: safeId },
    });

    return data;
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not update this service booking right now. Please try again later.",
    );

    dispatch({
      type: C.ADMIN_SERVICE_BOOKING_UPDATE_FAIL,
      payload,
      meta: { id: safeId },
    });

    throw error;
  }
};

export const deleteServiceBookingAdmin = (id) => async (dispatch) => {
  const safeId = normalizeString(id);

  try {
    dispatch({
      type: C.ADMIN_SERVICE_BOOKING_DELETE_REQUEST,
      meta: { id: safeId },
    });

    if (!safeId) {
      throw buildClientError(
        "Booking id is required.",
        "SERVICE_BOOKING_ID_REQUIRED",
      );
    }

    await api.delete(`/services/admin/bookings/${encodePath(safeId)}`);

    dispatch({
      type: C.ADMIN_SERVICE_BOOKING_DELETE_SUCCESS,
      payload: { id: safeId },
      meta: { id: safeId },
    });

    return { id: safeId };
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not delete this service booking right now. Please try again later.",
    );

    dispatch({
      type: C.ADMIN_SERVICE_BOOKING_DELETE_FAIL,
      payload,
      meta: { id: safeId },
    });

    throw error;
  }
};

export const listServiceOrdersAdmin =
  (status = "all") =>
  async (dispatch) => {
    try {
      dispatch({ type: C.ADMIN_SERVICE_ORDER_LIST_REQUEST });

      const safeStatus = normalizeString(status).toLowerCase() || "all";
      const { data } = await api.get("/services/admin/orders", {
        params: { status: safeStatus },
      });

      dispatch({
        type: C.ADMIN_SERVICE_ORDER_LIST_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payload = getErrorMessage(
        error,
        "We could not load service orders right now. Please try again later.",
      );

      dispatch({
        type: C.ADMIN_SERVICE_ORDER_LIST_FAIL,
        payload,
      });

      throw error;
    }
  };

export const getServiceOrderAdmin = (reference) => async (dispatch) => {
  const safeReference = normalizeString(reference);

  try {
    dispatch({
      type: C.ADMIN_SERVICE_ORDER_DETAILS_REQUEST,
      meta: { reference: safeReference },
    });

    if (!safeReference) {
      throw buildClientError(
        "Service order reference is required.",
        "SERVICE_ORDER_REFERENCE_REQUIRED",
      );
    }

    const { data } = await api.get(
      `/services/admin/orders/${encodePath(safeReference)}`,
    );

    dispatch({
      type: C.ADMIN_SERVICE_ORDER_DETAILS_SUCCESS,
      payload: data,
      meta: { reference: safeReference },
    });

    return data;
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not load this service order right now. Please try again later.",
    );

    dispatch({
      type: C.ADMIN_SERVICE_ORDER_DETAILS_FAIL,
      payload,
      meta: { reference: safeReference },
    });

    throw error;
  }
};

export const updateServiceOrderAdmin =
  (reference, patch) => async (dispatch) => {
    const safeReference = normalizeString(reference);

    try {
      dispatch({
        type: C.ADMIN_SERVICE_ORDER_UPDATE_REQUEST,
        meta: { reference: safeReference },
      });

      if (!safeReference) {
        throw buildClientError(
          "Service order reference is required.",
          "SERVICE_ORDER_REFERENCE_REQUIRED",
        );
      }

      const { data } = await api.patch(
        `/services/admin/orders/${encodePath(safeReference)}`,
        patch,
      );

      dispatch({
        type: C.ADMIN_SERVICE_ORDER_UPDATE_SUCCESS,
        payload: data,
        meta: { reference: safeReference },
      });

      dispatch({
        type: C.ADMIN_SERVICE_ORDER_DETAILS_SUCCESS,
        payload: data,
        meta: { reference: safeReference },
      });

      return data;
    } catch (error) {
      const payload = getErrorMessage(
        error,
        "We could not update this service order right now. Please try again later.",
      );

      dispatch({
        type: C.ADMIN_SERVICE_ORDER_UPDATE_FAIL,
        payload,
        meta: { reference: safeReference },
      });

      throw error;
    }
  };

export const addServiceOrderAdminMessage =
  (reference, message, { isInternal = false } = {}) =>
  async (dispatch) => {
    const safeReference = normalizeString(reference);

    try {
      dispatch({
        type: C.ADMIN_SERVICE_ORDER_MESSAGE_REQUEST,
        meta: { reference: safeReference },
      });

      if (!safeReference) {
        throw buildClientError(
          "Service order reference is required.",
          "SERVICE_ORDER_REFERENCE_REQUIRED",
        );
      }

      const { data } = await api.post(
        `/services/admin/orders/${encodePath(safeReference)}/messages`,
        {
          message: normalizeString(message),
          isInternal,
        },
      );

      dispatch({
        type: C.ADMIN_SERVICE_ORDER_MESSAGE_SUCCESS,
        payload: data,
        meta: { reference: safeReference },
      });

      dispatch({
        type: C.ADMIN_SERVICE_ORDER_DETAILS_SUCCESS,
        payload: data,
        meta: { reference: safeReference },
      });

      return data;
    } catch (error) {
      const payload = getErrorMessage(
        error,
        "We could not add that service order message right now. Please try again later.",
      );

      dispatch({
        type: C.ADMIN_SERVICE_ORDER_MESSAGE_FAIL,
        payload,
        meta: { reference: safeReference },
      });

      throw error;
    }
  };

export const createServiceOrderAdjustmentAdmin =
  (reference, adjustment) => async (dispatch) => {
    const safeReference = normalizeString(reference);

    try {
      dispatch({
        type: C.ADMIN_SERVICE_ORDER_ADJUSTMENT_REQUEST,
        meta: { reference: safeReference },
      });

      if (!safeReference) {
        throw buildClientError(
          "Service order reference is required.",
          "SERVICE_ORDER_REFERENCE_REQUIRED",
        );
      }

      const { data } = await api.post(
        `/services/admin/orders/${encodePath(safeReference)}/adjustments`,
        adjustment,
      );

      dispatch({
        type: C.ADMIN_SERVICE_ORDER_ADJUSTMENT_SUCCESS,
        payload: data,
        meta: { reference: safeReference },
      });

      dispatch({
        type: C.ADMIN_SERVICE_ORDER_DETAILS_SUCCESS,
        payload: data,
        meta: { reference: safeReference },
      });

      return data;
    } catch (error) {
      const payload = getErrorMessage(
        error,
        "We could not create that price adjustment right now. Please try again later.",
      );

      dispatch({
        type: C.ADMIN_SERVICE_ORDER_ADJUSTMENT_FAIL,
        payload,
        meta: { reference: safeReference },
      });

      throw error;
    }
  };
